import React, { useMemo } from 'react'
import { listTablePrefs, allKey } from 'app/constants'
import { flatten, map, pick, pipe, prop, uniqBy } from 'ramda'
import AsyncDropdown from 'core/elements/dropdown/AsyncDropdown'
import { projectAs } from 'utils/fp'
import useReactRouter from 'use-react-router'
import createCRUDComponents from 'core/helpers/createCRUDComponents'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import {
  Nodegroup,
  NodegroupInstance,
} from 'app/plugins/infrastructure/components/importedClusters/model'
import { listImportedClusters } from 'app/plugins/infrastructure/components/importedClusters/new-actions'
import { useSelector } from 'react-redux'
import { importedClustersSelector } from 'app/plugins/infrastructure/components/importedClusters/selectors'
import useListAction from 'core/hooks/useListAction'

type NodegroupInstanceItem = NodegroupInstance & { groupName: string }

const columns = [
  { id: 'groupName', label: 'Node Group' },
  { id: 'name', label: 'Node Name' },
  { id: 'location', label: 'Location' },
  { id: 'instanceTemplate', label: 'Instance Template' },
]

const defaultParams = {
  groupName: allKey,
}
const usePrefParams = createUsePrefParamsHook('GkeNodes', listTablePrefs)

const ListPage = ({ ListContainer }) => {
  return () => {
    const { params, getParamsUpdater } = usePrefParams(defaultParams)
    const { match } = useReactRouter()
    const { loading, reload } = useListAction(listImportedClusters)
    const clusters = useSelector(importedClustersSelector)
    const cluster = clusters.find((x) => x.uuid === match.params.id)

    const { nodeGroups = [] } = cluster
    const nodes = pipe<Nodegroup[], NodegroupInstanceItem[][], NodegroupInstanceItem[]>(
      map((nodeGroup) =>
        nodeGroup?.instances?.map((instance) => ({
          ...instance,
          groupName: nodeGroup?.name,
        })),
      ),
      flatten,
    )(nodeGroups)

    const options = useMemo(
      () => projectAs({ label: 'groupName', value: 'groupName' }, uniqBy(prop('groupName'), nodes)),
      [nodeGroups],
    )
    const filteredNodes = nodes.filter((node) => {
      if (params.groupName === allKey) {
        return true
      } else {
        return params.groupName === node.groupName
      }
    })
    return (
      <ListContainer
        loading={loading}
        reload={reload}
        data={filteredNodes}
        getParamsUpdater={getParamsUpdater}
        filters={
          <>
            <AsyncDropdown
              label="Node Group"
              onChange={getParamsUpdater('groupName')}
              value={params.groupName}
              items={options}
            />
          </>
        }
        {...pick(listTablePrefs, params)}
      />
    )
  }
}

export const options = {
  columns,
  uniqueIdentifier: 'id',
  multiSelection: false,
  showBreadcrumbs: false,
  ListPage,
}

const components = createCRUDComponents(options)

export default components.ListPage
